
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Toaster } from "../../../Components/ui/toaster";
import { setCreateBreadCrumb } from "../../../State/slices/AdvertiserAccountSlice";
import { Button } from "../../../Components/ui/button";
import { Card } from "../../../Components/ui/card";
import { Label } from "../../../Components/ui/label";
import { useToast } from "../../../Components/ui/use-toast";
import { Switch } from "../../../Components/ui/switch";
import {
  Select,
  SelectItem,
  SelectTrigger,
  SelectContent,
  SelectValue,
} from "../../../Components/ui/select";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "src/Components/ui/dialog";
import { Input } from "../../../Components/ui/input";
import { Currency } from "lucide-react";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress";
import { RootState } from "@/src/State/store";

const CreatePlans: React.FC = () => {
  const adminApiUrl = useSelector((state: RootState) => state.authentication.adminUrl);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const plansList = location.state?.plansList || [];
  const from = location.state?.from || "/adminNavbar/plans";
  const { toast } = useToast();
  const { plan_id, name, messages, per_message, price, planDesc: initialPlanDesc, initialCountrySymbol, isFreeTier } = location.state || {};

  const [planName, setPlanName] = useState<string>(name || "");
  const [planMessage, setPlanMessage] = useState<string>(messages || "");
  const [perMessage, setPerMessage] = useState<string>(per_message || "");
  const [planPrice, setPlanPrice] = useState<string>(price != null ? String(price) : "");
  const [countrySymbol, setCountrySymbol] = useState<string>(initialCountrySymbol || "USD");
  const [currencyList, setCurrencyList] = useState<string[]>([]);
  const [planNameError, setPlanNameError] = useState("");
  const [planDesc, setPlanDesc] = useState<string>(initialPlanDesc || "");
  const [planDescError, setPlanDescError] = useState('');
  const [planMessageError, setPlanMessageError] = useState("");
  const [perMessageError, setPerMessageError] = useState("");
  const [planPriceError, setPlanPriceError] = useState("");
  const [countrySymbolError, setCountrySymbolError] = useState("");
  const [displayPlanPrice, setDisplayPlanPrice] = useState<string>(price != null ? Number(price).toLocaleString() : "0");
  const [displayPlanMessage, setDisplayPlanMessage] = useState<string>(messages ? Number(messages).toLocaleString() : "");
  const apiUrlAdvAcc = useSelector((state: RootState) => state.authentication.apiURL);
  const accountId = useSelector((state: RootState) => state.authentication.account_id);
  const workspaceId = useSelector((state: RootState) => state.authentication.workspace_id);

  useEffect(() => {
    let authToken = localStorage.getItem("authToken");

    if (!authToken) {
      authToken = "mock-token";
      localStorage.setItem("authToken", authToken);
    }

    if (plan_id) {
      setPlanName(name || "");
      setPlanMessage(messages || "");
      setPlanPrice(price != null ? String(price) : "");
      setPerMessage(per_message || "");
      setPlanDesc(initialPlanDesc || "");
      setCountrySymbol(initialCountrySymbol || "USD");
      setDisplayPlanMessage(messages ? Number(messages).toLocaleString() : "");
      setDisplayPlanPrice(price != null ? Number(price).toLocaleString() : "0");
    }
  }, [plan_id, name, messages, per_message, price, initialPlanDesc, initialCountrySymbol, location, apiUrlAdvAcc, adminApiUrl]);

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!apiUrlAdvAcc) return;
      try {
        const [currencyResponse] = await Promise.all([
          axios.get(`${apiUrlAdvAcc}/GetCurrencyNames`),
        ]);

        if (currencyResponse.data.status === "Success") {
          setCurrencyList(currencyResponse.data.currencyList || []);
        } else {
          toast({
            title: "Error",
            description: "Failed to fetch currency list.",
            duration: 3000,
          });
        }
      } catch (error) {
        console.error("Error during initial data load:", error);
        toast({
          title: "Error",
          description: "Failed to load initial data.",
          duration: 3000,
        });
      }
    };

    fetchInitialData();
  }, [apiUrlAdvAcc, toast]);

  useEffect(() => {
    const calculatePerMessage = () => {
      const messagesValue = parseFloat(planMessage);
      const priceValue = parseFloat(planPrice);
      if (!isNaN(messagesValue) && !isNaN(priceValue) && messagesValue !== 0) {
        const perMsg = priceValue / messagesValue;
        setPerMessage(perMsg.toFixed(4));
        setPerMessageError("");
      } else {
        setPerMessage("");
      }
    };
    calculatePerMessage();
  }, [planMessage, planPrice]);

  const handlePlanNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.trim() === "") {
      setPlanNameError("Plan Name should not be empty.");
      setPlanName(value);
      return;
    }

    const firstChar = value.charAt(0);
    const lastChar = value.charAt(value.length - 1);
    const alphanumericRegex = /^[a-zA-Z0-9]$/;

    if (!alphanumericRegex.test(firstChar)) {
      setPlanNameError("First character must be a letter or number.");
    } else if (!alphanumericRegex.test(lastChar)) {
      setPlanNameError("Last character must be a letter or number.");
    } else {
      setPlanNameError("");
    }

    setPlanName(value);
  };

  const handlePlanMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/,/g, "");
    if (/^\d*$/.test(value)) {
      setPlanMessage(value);
      setDisplayPlanMessage(value ? Number(value).toLocaleString() : "");
      setPlanMessageError("");
    } else {
      setPlanMessageError("Only numbers are allowed.");
    }
  };

  const handlePerMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*([.,]?\d*)?$/.test(value)) {
      setPerMessage(value);
      setPerMessageError("");
    } else {
      setPerMessageError("Only numbers and a single decimal point or comma are allowed.");
    }
  };

  const handlePlanPriceChange = (num: string) => {
    let value = num.replace(/,/g, "");
    if (value === "") {
      setPlanPrice("");
      setDisplayPlanPrice("");
      setPlanPriceError("");
      return;
    }
    if (/^\d*$/.test(value)) {
      const numValue = Number(value);
      if (numValue > 999999) {
        setPlanPriceError("Price should not exceed 999,999.");
      } else if (numValue === 0 && !isFreeTier) {
        setPlanPriceError("Price must be greater than 0 for paid plans.");
      } else {
        setPlanPrice(value);
        setDisplayPlanPrice(numValue.toLocaleString());
        setPlanPriceError("");
      }
    } else {
      setPlanPriceError("Only numbers are allowed.");
    }
  };

  const handleCountrySymbolChange = (value: string) => {
    if (!value.trim()) {
      setCountrySymbolError("Please select a currency.");
    } else {
      setCountrySymbolError("");
    }
    setCountrySymbol(value);
  };

  const handleEdit = async () => {
    let isValid = true;
    setPlanNameError("");
    setPlanMessageError("");
    setPerMessageError("");
    setPlanPriceError("");
    setCountrySymbolError("");

    if (!planName.trim()) {
      setPlanNameError("Plan name is required.");
      isValid = false;
    } else {
      const firstCharRegex = /^[A-Za-z]/;
      const middleRegex = /^[A-Za-z\s]+$/;
      const lastCharRegex = /^[A-Za-z]$/;
      const firstChar = planName.charAt(0);
      const lastChar = planName.charAt(planName.length - 1);

      if (!firstCharRegex.test(firstChar)) {
        setPlanNameError("First character should be a letter.");
        isValid = false;
      } else if (!middleRegex.test(planName)) {
        setPlanNameError("Only letters and spaces are allowed.");
        isValid = false;
      } else if (!lastCharRegex.test(lastChar)) {
        setPlanNameError("Last character should be a letter.");
        isValid = false;
      }

      const isDuplicate = plansList.some(
        (plan: any) =>
          plan.name?.trim().toLowerCase() === planName.trim().toLowerCase() &&
          (!isFreeTier || plan.plan_id !== plan_id)
      );
      if (isDuplicate) {
        setPlanNameError("Plan name already exists.");
        isValid = false;
      }
    }

    if (!planMessage.trim() || isNaN(parseFloat(planMessage))) {
      setPlanMessageError("Messages must be a valid number.");
      isValid = false;
    }

    if (!perMessage.trim() || isNaN(parseFloat(perMessage))) {
      setPerMessageError("Per message price must be a valid number.");
      isValid = false;
    }

    const amount = parseFloat(planPrice);
    if (isNaN(amount)) {
      setPlanPriceError("Price must be a valid number.");
      isValid = false;
    } else if (amount > 999999) {
      setPlanPriceError("Price should not exceed 999,999.");
      isValid = false;
    } else if (amount <= 0 && !isFreeTier) {
      setPlanPriceError("Price must be greater than 0 for paid plans.");
      isValid = false;
    }

    if (!plan_id) {
      setPlanNameError("Invalid plan ID.");
      isValid = false;
    }

    if (!isFreeTier && !countrySymbol.trim()) {
      setCountrySymbolError("Currency is required.");
      isValid = false;
    }

    if (!isValid) {
      toast({
        title: "Invalid Input",
        description: "Please check the highlighted fields and provide valid information.",
        duration: 3000,
      });
      return;
    }

    setIsLoading(true);

    try {
      if (isFreeTier) {
        const data = {
          planId: plan_id,
          planName: planName,
          planAmount: amount,
          msgCount: Number(planMessage),
          perMessage: perMessage,
          planDesc: planDesc,
          workspaceId: workspaceId,
          accountId: accountId
        };
        const response = await axios.post(`${adminApiUrl}/UpdateFreeTierPlanDetails`, data);
        if (!response.data || typeof response.data !== "object") {
          console.error("Invalid response from server:", response.data);
          toast({
            title: "Error",
            description: "Invalid response from server. Please try again later.",
            duration: 3000,
          });
          setIsLoading(false);
          return;
        }

        if (response.data.status === "Success") {
          toast({
            title: "Success",
            description: "Starter plan updated successfully.",
            duration: 3000,
          });
          setTimeout(() => {
            dispatch(setCreateBreadCrumb(false));
            navigate(from);
          }, 1000);
        } else {
          console.error("Starter plan update failed:", response.data.Status_Description || "Unknown error");
          toast({
            title: "Error",
            description: response.data.Status_Description || "Free Tier plan update failed.",
            duration: 3000,
          });
        }
      } else {
        const data = {
          planId: plan_id,
          msgCount: parseInt(planMessage, 10),
          planAmount: planPrice,
          currency: countrySymbol,
          per_message: parseFloat(perMessage),
          planName: planName,
          planDesc: planDesc,
        };

        const response = await axios.put(`${apiUrlAdvAcc}/UpdateBillingFeature`, data);

        if (!response.data || typeof response.data !== "object") {
          console.error("Invalid response from server:", response.data);
          toast({
            title: "Error",
            description: "Invalid response from server. Please try again later.",
            duration: 3000,
          });
          setIsLoading(false);
          return;
        }

        if (response.data.status === "Success") {
          toast({
            title: "Success",
            description: "Enterprise plan updated successfully.",
            duration: 3000,
          });
          setTimeout(() => {
            dispatch(setCreateBreadCrumb(false));
            navigate(from);
          }, 1000);
        } else {
          console.error("Enterprise plan update failed:", response.data.Status_Description || "Unknown error");
          toast({
            title: "Error",
            description: response.data.Status_Description || "Paid Tier plan update failed.",
            duration: 3000,
          });
        }
      }
    } catch (error) {
      console.error("Error updating plan:", error);
      if (axios.isAxiosError(error)) {
        console.error("Axios Error Details:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            data: error.config?.data,
          },
        });
        const errorMessage =
          error.response?.data?.Status_Description ||
          error.response?.data?.message ||
          error.message ||
          "Failed to update plan.";
        toast({
          title: "Error",
          description: errorMessage,
          duration: 3000,
        });
      } else {
        toast({
          title: "Error",
          description: "An unexpected error occurred while updating the plan.",
          duration: 3000,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleNameValidation = () => {
    const isExist = plansList.some(
      (plan: any) =>
        plan.countrySymbol?.trim().toLowerCase() === countrySymbol?.trim().toLowerCase() &&
        plan.name?.trim().toLowerCase() === planName?.trim().toLowerCase()
    );

    if (isExist) {
      setPlanNameError("Plan name already existed for this currency label");
      return true;
    } else {
      setPlanNameError("");
      return false;
    }
  };

  const handleSubmit = async () => {
    if (handleNameValidation()) {
      return;
    }

    let isValid = true;
    setPlanNameError("");
    setPlanMessageError("");
    setPerMessageError("");
    setPlanPriceError("");
    setCountrySymbolError("");

    const firstCharRegex = /^[A-Za-z]/;
    const middleRegex = /^[A-Za-z\s]+$/;
    const lastCharRegex = /^[A-Za-z]$/;
    const firstChar = planName.charAt(0);
    const lastChar = planName.charAt(planName.length - 1);

    if (!planName.trim()) {
      setPlanNameError("Plan name is required.");
      isValid = false;
    } else if (!firstCharRegex.test(firstChar)) {
      setPlanNameError("First character should be a letter.");
      isValid = false;
    } else if (!middleRegex.test(planName)) {
      setPlanNameError("Only letters and spaces are allowed.");
      isValid = false;
    } else if (!lastCharRegex.test(lastChar)) {
      setPlanNameError("Last character should be a letter.");
      isValid = false;
    }

    if (!planMessage) {
      setPlanMessageError("Included messages are required.");
      isValid = false;
    }
    if (!perMessage) {
      setPerMessageError("Per message price is required.");
      isValid = false;
    }
    if (!planPrice) {
      setPlanPriceError("Price is required.");
      isValid = false;
    }
    if (!countrySymbol) {
      setCountrySymbolError("Currency is required.");
      isValid = false;
    }

    if (!isValid) {
      toast({
        title: "Invalid Input",
        description: "Please check the highlighted fields and provide valid information.",
        duration: 2000,
      });
      return;
    }

    const data = {
      planName: planName,
      planDesc: planDesc,
      currency: countrySymbol,
      planAmount: Number(planPrice),
      msgCount: Number(planMessage),
      createdBy: accountId,
      workspaceId: workspaceId,
    };

    setIsLoading(true);
    try {
      const response = await axios.post(`${adminApiUrl}/InsertNewPlan`, data);
      if (response.data.status === "Success") {
        toast({
          title: "Success",
          description: "Plan created successfully.",
          duration: 3000,
        });
        setTimeout(() => {
          dispatch(setCreateBreadCrumb(false));
          navigate(from);
        }, 1000);
      } else {
        console.error("Upload failed:", response.data.Status_Description || "Unknown error");
        toast({
          title: "Error",
          description: response.data.Status_Description || "Plan creation failed.",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error("Error creating plan:", error);
      toast({
        title: "Error",
        description: "Failed to create plan.",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDiscard = () => {
    dispatch(setCreateBreadCrumb(false));
    navigate(from);
    setIsDialogOpen(false);
  };

  const discardMessage = plan_id
    ? "Do you want to discard the plan's update?"
    : "Do you want to discard the plan?";

  return (
    <>
      <div className="overflow-y-auto ml-[-7px]">
        {isLoading && (
          <div className="absolute inset-0 bg-gray-500/50 flex items-center justify-center z-50">
            <CircularProgress className="text-primary" />
          </div>
        )}
        <Toaster />
        <div className="fixed flex justify-end items-end right-0 top-[-15px] z-20 p-4 mr-2 gap-2">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="w-[70px] px-3 py-1 text-sm border-red-500 text-red-500 hover:bg-red-500 hover:text-white mt-[10px]" variant="outline">
                Discard
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogTitle className="text-18px font-semibold text-[#09090B] mb-2">
                Discard Changes
              </DialogTitle>
              <DialogDescription className="text-14px font-medium text-[#71717A]">
                {discardMessage}
              </DialogDescription>
              <div className="flex justify-end gap-4">
                <Button variant="outline" className="px-4 py-2 w-24" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button className="w-24 px-3 py-1 text-sm bg-red-500 text-white hover:bg-red-600" onClick={handleDiscard}>
                  Discard
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Button className="w-[80px] text-[#F8FAFC] mt-[10px]" onClick={() => (plan_id ? handleEdit() : handleSubmit())}>
            {plan_id ? "Update" : "Submit"}
          </Button>
        </div>

        <div className="ml-4">
          <Card className="w-[580px] mt-2 p-4">
            <div className="text-left">
              <h3 className="text-md font-semibold text-left">{plan_id ? "Free Tier" : "Add New Plan"}</h3>
              <div className="mt-4">
                <Label htmlFor="planName" className="mt-8">
                  Name
                </Label>
                <Input
                  id="planName"
                  type="text"
                  placeholder="e.g. Executive"
                  value={planName}
                  onChange={handlePlanNameChange}
                  className="mt-2 text-[#020617]"
                />
                {planNameError && <p className="text-red-500 text-xs font-medium mt-1 mb-2 font-sans italic ml-1">{planNameError}</p>}
              </div>
              <div className="mt-4">
                <Label htmlFor="planDesc" className="mt-8">
                  Plan Description
                </Label>
                <textarea
                  id="planDesc"
                  placeholder="Description for the plan"
                  value={planDesc}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[A-Za-z\s]*$/.test(value)) {
                      setPlanDesc(value);
                      setPlanDescError("");
                    } else {
                      setPlanDesc(value);
                      setPlanDescError("Only alphabets and spaces are allowed.");
                    }
                  }}
                  rows={4}
                  className="mt-2 w-full border border-gray-300 rounded-md p-2 text-sm text-[#020617] placeholder:text-[#64748B] focus:outline-none focus:ring-1 focus:ring-gray-400 resize-none"
                />

                {planDescError && (
                  <p className="text-red-500 text-sm mt-1">{planDescError}</p>
                )}
              </div>

              <div className="mt-4">
                <Label htmlFor="planMessage" className="mt-2">
                  Included Messages
                </Label>
                <Input
                  id="planMessage"
                  type="text"
                  placeholder="e.g. 100,000"
                  value={displayPlanMessage}
                  onChange={handlePlanMessageChange}
                  className="mt-2 text-[#020617]"
                />
                {planMessageError && <p className="text-red-500 text-xs font-medium mt-1 mb-2 font-sans italic ml-1">{planMessageError}</p>}
              </div>
              <div className="mt-4">
                <Label htmlFor="planPrice" className="mt-2">
                  Price
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="planPrice"
                    type="text"
                    placeholder="e.g.1,000"
                    value={displayPlanPrice}
                    onChange={(e) => handlePlanPriceChange(e.target.value)}
                    className="mt-2 text-[#020617] "
                  />
                  {!plan_id && (
                    <Select value={countrySymbol} onValueChange={(value) => handleCountrySymbolChange(value)}>
                      <SelectTrigger className="text-[#020617] mt-2">
                        <SelectValue className="text-gray-500" placeholder={countrySymbol || "Select currency"} />
                      </SelectTrigger>
                      <SelectContent>
                        {currencyList.map((currency) => (
                          <SelectItem className="text-gray-500 cursor-pointer" key={currency} value={currency}>
                            {currency}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                {countrySymbolError && <p className="text-red-500 text-xs font-medium mt-1 mb-2 font-sans italic ml-1">{countrySymbolError}</p>}
                {planPriceError && <p className="text-red-500 text-xs font-medium mt-1 mb-2 font-sans italic ml-1">{planPriceError}</p>}
              </div>
              <div className="mt-4">
                <Label htmlFor="permessage" className="mt-2">
                  Per Messages Price
                </Label>
                <Input
                  id="per_message"
                  type="text"
                  placeholder="1.0"
                  value={perMessage}
                  onChange={handlePerMessageChange}
                  readOnly
                  className="mt-2 text-[#020617] cursor-not-allowed focus:outline-none bg-gray-100"
                />
                {perMessageError && <p className="text-red-500 text-xs font-medium mt-1 mb-2 font-sans italic ml-1">{perMessageError}</p>}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default CreatePlans;
